import { reactive, inject, computed, provide } from '@vue/composition-api'
import { updateIdentifiableList } from '@/shared/utils'
import { Textbook, TextbookIssue, Volume } from '@/shared/types'
import {
  getUserTextbooks,
  deleteTextbook as deleteTextbookApi,
  deleteTextbookIssue as deleteTextbookIssueApi,
  deleteVolume as deleteVolumeApi
} from '@/shared/api'

const stateSymbol = Symbol('textbook-state')

interface StateType {
  activeTextbookId: string
  activeTextbookIssueId: string
  loading: boolean
  textbooks: Array<Textbook>
}

const initialState: StateType = {
  activeTextbookId: '',
  activeTextbookIssueId: '',
  loading: false,
  textbooks: [],
}

const createStore = () => {
  const state = reactive<StateType>({ ...initialState })

  const getters = {
    activeTextbook: computed(() =>
      state.textbooks.find((el: Textbook) => el.id === state.activeTextbookId)
    ),
    activeTextbookIssue: computed((): TextbookIssue | null =>
      getters.activeTextbook.value
        ? getters.activeTextbook.value.issues.find(
            (el: TextbookIssue) => el.id === state.activeTextbookIssueId
          )
        : null
    ),
    loading: computed(() => state.loading),
    textbooks: computed<Array<Textbook>>(() => state.textbooks),
    textbookById: (id: string) =>
      computed(() => state.textbooks.find((el: Textbook) => el.id === id)),
  }

  const actions = {
    addTextbooks: (aTextbookList: Array<Textbook>) => {
      state.textbooks = [...state.textbooks, ...aTextbookList]
    },
    addTextbookIssue: (aTextbookId: string, aTextbookIssue: TextbookIssue) => {
      const newTextbook = getters.textbookById(aTextbookId).value
      newTextbook.issues.push(aTextbookIssue)
      actions.updateTextbook(newTextbook)
    },
    deleteCategories: (ids: Array<string>) => {
      const newTextbooks = state.textbooks.map((textbook: Textbook) => ({
        ...textbook,
        categories: textbook.categories.filter(el => !ids.includes(el.id))
      })
      
      )
      state.textbooks = [...newTextbooks]
    },
    deleteTextbook: async (id: string) => {
      state.loading = true
      try {
        await deleteTextbookApi(id)
        state.textbooks = state.textbooks.filter(
          (textbook: Textbook) => textbook.id !== id
        )
        state.loading = false
      } catch (e) {
        state.loading = false
        throw e
      }
    },
    deleteTextbookIssue: async (id: string) => {
      state.loading = true
      try {
        await deleteTextbookIssueApi(id)
        state.textbooks = state.textbooks.map((textbook: Textbook) => ({
          ...textbook,
          issues: textbook.issues.filter(
            (issue: TextbookIssue) => issue.id !== id
          ),
        }))
        state.loading = false
      } catch (e) {
        state.loading = false
        throw e
      }
    },
    loadTextbooks: async () => {
      state.loading = true
      try {
        const textbooks = await getUserTextbooks()
        state.textbooks = [...textbooks]
        state.loading = false
      } catch (e) {
        state.loading = false
        throw e
      }
    },
    updateActiveTextbook: (aTextbookId: string) => {
      state.activeTextbookId = aTextbookId
    },
    updateActiveTextbookIssue: (aTextbookIssueId: string) => {
      state.activeTextbookIssueId = aTextbookIssueId
    },
    updateTextbook: (aTextbook: Textbook) => {
      state.textbooks = updateIdentifiableList(state.textbooks, aTextbook)
    },
    updateTextbooks: (aTextbookList: Array<Textbook>) => {
      state.textbooks = aTextbookList
    },
    updateTextbookIssue: (aTextbookIssue: TextbookIssue | null) => {
      state.textbooks = state.textbooks.map((textbook: Textbook) => ({
        ...textbook,
        issues: updateIdentifiableList(textbook.issues, aTextbookIssue),
      }))
    },
    addVolume: (aTextbookId: string, aVolume: Volume) => {
      const newTextbook = getters.textbookById(aTextbookId).value
      newTextbook.volumes.push(aVolume)
      actions.updateTextbook(newTextbook)
    },
    updateVolume: (aVolume: Volume | null) => {
      state.textbooks = state.textbooks.map((textbook: Textbook) => ({
        ...textbook,
        volumes: updateIdentifiableList(textbook.volumes, aVolume),
      }))
    },
    deleteVolume: async (textbookId: string, volumeId: string) => {
      state.loading = true
      try {
        await deleteVolumeApi(textbookId, volumeId)
        state.textbooks = state.textbooks.map((textbook: Textbook) => ({
          ...textbook,
          volumes: textbook.volumes.filter(
            (volume: Volume) => volume.id !== volumeId
          ),
        }))
        state.loading = false
      } catch (e) {
        state.loading = false
        throw e
      }
    },
  }

  return {
    state: computed(() => state),
    getters,
    actions,
  }
}

export const provideStore = () => provide(stateSymbol, createStore())
export const useStore = () =>
  inject(stateSymbol) as ReturnType<typeof createStore>
